import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { App, AppInfo } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import * as Sentry from '@sentry/angular';

import { select, Store } from '@ngrx/store';
import { catchError } from 'rxjs';
import * as fromRoot from '@app/app.reducers';

import { LocalStorageKeys } from '@app/app.constants';
import { environment } from 'src/environments/environment';

import { TrackingDetails } from '../models/Account';

@Injectable({
  providedIn: 'root'
})
export class TrackingService {
  private currentAccountID: string;
  private currentLocation: string;
  private isCIActive: boolean;
  private deviceInfo: AppInfo;

  constructor(
    private http: HttpClient,
    private store: Store
  ) {
    const localStorageValue = localStorage.getItem(LocalStorageKeys.CURRENT_ACCOUNT_ID);
    this.store.pipe(select(fromRoot.selectSelectedAccount)).subscribe((account) => {
      this.currentAccountID = !account?.id && localStorageValue ? localStorageValue : account.id;
    });
    this.store.pipe(select(fromRoot.selectCrowdInvestorAccountActive)).subscribe((value) => {
      this.isCIActive = value;
    });
    if (Capacitor.isNativePlatform()) {
      void App.getInfo().then((deviceInfo) => {
        this.deviceInfo = deviceInfo;
      });
    }
  }

  trackActivity(activity: string) {
    if (!environment.production) {
      // eslint-disable-next-line no-console
      console.log(`[Lead activity] Activity clicked: ${activity}`);
    }
    this.http
      .post(`${environment.apiUrl}/journey/action`, this.getTrackingDetails(`Activity clicked: ${activity}`))
      .pipe(catchError((error) => Sentry.captureException(new Error('Error tracking activity'), { extra: { ...error } })))
      .subscribe();
  }

  trackView(pageView: string) {
    this.currentLocation = pageView;
    if (!environment.production) {
      // eslint-disable-next-line no-console
      console.log(`[Lead activity] Page visited: ${pageView}`);
    }
    this.http
      .post(`${environment.apiUrl}/journey/action`, this.getTrackingDetails(`Page visited: ${pageView}`))
      .pipe(catchError((error) => Sentry.captureException(new Error('Error tracking page view'), { extra: { ...error } })))
      .subscribe();
  }

  trackLaunch(activity: string) {
    if (!environment.production) {
      // eslint-disable-next-line no-console
      console.log(`[Lead activity] Activity clicked: ${activity}`);
    }
    this.http
      .post(`${environment.apiUrl}/journey/action`, this.getTrackingDetails(`Launched: ${activity}`))
      .pipe(catchError((error) => Sentry.captureException(new Error('Error tracking launch'), { extra: { ...error } })))
      .subscribe();
  }

  trackAppVersion() {
    const activity = `Version ${this.deviceInfo?.version} - ${this.deviceInfo?.build}`;
    this.http
      .post(`${environment.apiUrl}/journey/action`, this.getTrackingDetails(activity))
      .pipe(catchError((error) => Sentry.captureException(new Error('Error tracking app version'), { extra: { ...error } })))
      .subscribe();
  }

  getTrackingDetails(content: string): TrackingDetails {
    const trackingDetails = {
      location: this.currentLocation,
      platform: 'mobile-app'
    };
    if (this.isCIActive || this.currentAccountID === '') {
      return { content: `CI: ${content}`, ...trackingDetails };
    }
    return { content, ...trackingDetails, account_id: this.currentAccountID };
  }
}
